/**
 * 官媒舱 json 列的**唯一解析层**。
 *
 * 后端(gscq-api ReporterDtos)把 ai 产的 material/plan/sections/warnings… 原文透传成
 * string 列,形状真源是 gscq-ai `app/modules/reporter/domain/models.py`(规格 §5.1)。
 * 这里逐字段容错解析:形状不对**整块返回 null / 空数组**,页面按「没有这块数据」渲染,
 * 不让一条坏 JSON 白屏整页。禁止在组件里散写 JSON.parse(第二份口径必漂)。
 */

export type MaterialStatus = "explicit" | "inferred" | "uncertain";

export interface MaterialField {
  key: string;
  value: string;
  source: string;
  status: MaterialStatus;
  confidence: "high" | "medium" | "low";
}

export interface MissingField {
  field: string;
  reason: string;
  suggestion: string;
}

export interface MaterialExtract {
  genre: string;
  summary: string;
  completeness: string;
  fields: MaterialField[];
  missingFields: MissingField[];
  uncertainFields: { field: string; text: string; risk: string; suggestion: string }[];
  warnings: string[];
  suggestions: string[];
}

export interface EvidenceRef {
  key: string;
  label: string;
  value: string;
  origin: "material" | "product" | "enterprise" | "knowledge";
  source: string;
  /** origin="knowledge" 时的知识库文档 id(溯源反查;其余来源为空串)。 */
  docId: string;
}

export interface PlanSection {
  id: string;
  heading: string;
  purpose: string;
  evidenceKeys: string[];
}

export interface WritingPlan {
  genre: string;
  genreName: string;
  tone: string;
  targetWords: number;
  objective: string;
  audience: string;
  coreMessage: string;
  evidenceMap: EvidenceRef[];
  sections: PlanSection[];
  missingFacts: MissingField[];
  /** 无来源被剔除项 —— 留痕可见,规划面板必须渲染(F4:剔除不是静默丢)。 */
  droppedEvidence: string[];
  /** 本次已选拓展关键词回显(R2)。 */
  keywords: string[];
}

export interface ArticleSection {
  id: string;
  heading: string;
  content: string;
}

export interface StructuredWarning {
  severity: "HIGH" | "MEDIUM" | "INFO";
  category: string;
  issue: string;
  action: string;
  suggestion: string;
}

export interface ImageSuggestion {
  scene: string;
  keywords: string[];
  style: string;
  /** 段落锚(批二):插图插排到该段之后;封面/真无锚为空串。 */
  sectionId: string;
  /** 画幅建议(批二):"16:9"|"1:1"|"3:4";空串=按槽位默认。 */
  ratio: string;
}

/* ────────────────────────── helpers ────────────────────────── */

function obj(raw: string | null | undefined): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function arr(raw: string | null | undefined): unknown[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

const s = (v: unknown): string => (typeof v === "string" ? v : "");
const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

function missingList(v: unknown): MissingField[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => ({ field: s(x.field), reason: s(x.reason), suggestion: s(x.suggestion) }))
    .filter((x) => x.field);
}

/* ────────────────────────── 四块解析 ────────────────────────── */

const STATUSES = new Set(["explicit", "inferred", "uncertain"]);
const CONFIDENCES = new Set(["high", "medium", "low"]);

export function parseMaterial(raw: string | null | undefined): MaterialExtract | null {
  const o = obj(raw);
  if (!o) return null;
  const fieldsObj =
    o.fields && typeof o.fields === "object" && !Array.isArray(o.fields)
      ? (o.fields as Record<string, unknown>)
      : {};
  const fields: MaterialField[] = [];
  for (const [key, v] of Object.entries(fieldsObj)) {
    if (!v || typeof v !== "object") continue;
    const f = v as Record<string, unknown>;
    fields.push({
      key,
      value: s(f.value),
      source: s(f.source),
      status: STATUSES.has(s(f.status)) ? (s(f.status) as MaterialStatus) : "explicit",
      confidence: CONFIDENCES.has(s(f.confidence))
        ? (s(f.confidence) as MaterialField["confidence"])
        : "high",
    });
  }
  return {
    genre: s(o.genre),
    summary: s(o.summary),
    completeness: s(o.completeness),
    fields,
    missingFields: missingList(o.missingFields),
    uncertainFields: Array.isArray(o.uncertainFields)
      ? o.uncertainFields
          .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
          .map((x) => ({
            field: s(x.field),
            text: s(x.text),
            risk: s(x.risk),
            suggestion: s(x.suggestion),
          }))
      : [],
    warnings: strList(o.warnings),
    suggestions: strList(o.suggestions),
  };
}

const ORIGINS = new Set(["material", "product", "enterprise", "knowledge"]);

export function parsePlan(raw: string | null | undefined): WritingPlan | null {
  const o = obj(raw);
  if (!o) return null;
  return {
    genre: s(o.genre),
    genreName: s(o.genreName),
    tone: s(o.tone),
    targetWords: typeof o.targetWords === "number" ? o.targetWords : 0,
    objective: s(o.objective),
    audience: s(o.audience),
    coreMessage: s(o.coreMessage),
    evidenceMap: Array.isArray(o.evidenceMap)
      ? o.evidenceMap
          .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
          .map((x) => ({
            key: s(x.key),
            label: s(x.label),
            value: s(x.value),
            origin: ORIGINS.has(s(x.origin)) ? (s(x.origin) as EvidenceRef["origin"]) : "material",
            source: s(x.source),
            docId: s(x.docId),
          }))
      : [],
    sections: Array.isArray(o.sections)
      ? o.sections
          .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
          .map((x) => ({
            id: s(x.id),
            heading: s(x.heading),
            purpose: s(x.purpose),
            evidenceKeys: strList(x.evidenceKeys),
          }))
      : [],
    missingFacts: missingList(o.missingFacts),
    droppedEvidence: strList(o.droppedEvidence),
    keywords: strList(o.keywords),
  };
}

export function parseSections(raw: string | null | undefined): ArticleSection[] {
  return arr(raw)
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => ({ id: s(x.id), heading: s(x.heading), content: s(x.content) }))
    .filter((x) => x.content || x.heading);
}

export function parseTitles(raw: string | null | undefined): string[] {
  return strList(arr(raw));
}

export function parseTags(raw: string | null | undefined): string[] {
  return strList(arr(raw));
}

const SEVERITIES = new Set(["HIGH", "MEDIUM", "INFO"]);

/** severity 由产生方给定(StructuredWarning);不认识的值按 INFO 展示但不丢条目。 */
export function parseWarnings(raw: string | null | undefined): StructuredWarning[] {
  return arr(raw)
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => ({
      severity: SEVERITIES.has(s(x.severity))
        ? (s(x.severity) as StructuredWarning["severity"])
        : "INFO",
      category: s(x.category),
      issue: s(x.issue),
      action: s(x.action),
      suggestion: s(x.suggestion),
    }))
    .filter((x) => x.issue);
}

export function parseImageSuggestions(raw: string | null | undefined): ImageSuggestion[] {
  return arr(raw)
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => ({
      scene: s(x.scene),
      keywords: strList(x.keywords),
      style: s(x.style),
      sectionId: s(x.sectionId),
      ratio: s(x.ratio),
    }))
    .filter((x) => x.scene);
}

/** dimScoresJson:{维度: 分} 平面对象。 */
export function parseDimScores(raw: string | null | undefined): Record<string, number> {
  const o = obj(raw);
  if (!o) return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(o)) if (typeof v === "number") out[k] = v;
  return out;
}

export function parseStringList(raw: string | null | undefined): string[] {
  return strList(arr(raw));
}
